import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react';
import logoIcon from '@/assets/logo-icon.webp';

export const industries = [
  { name: 'Healthcare', path: '/industries/healthcare', icon: '🏥' },
  { name: 'Real Estate', path: '/industries/real-estate', icon: '🏠' },
  { name: 'Restaurants', path: '/industries/restaurants', icon: '🍽️' },
  { name: 'Education', path: '/industries/education', icon: '📚' },
  { name: 'Salon & Wellness', path: '/industries/salon-wellness', icon: '💇' },
  { name: 'Automobile', path: '/industries/automobile', icon: '🚗' },
  { name: 'Home Services', path: '/industries/home-services', icon: '🔧' },
];

interface MarketingHeaderProps {
  showIndustries?: boolean;
  showPricing?: boolean;
  showHowItWorks?: boolean;
}

export default function MarketingHeader({ 
  showIndustries = true, 
  showPricing = true, 
  showHowItWorks = true 
}: MarketingHeaderProps) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [industriesDropdownOpen, setIndustriesDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIndustriesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If not on the landing page, navigate to it with the hash
      navigate(`/#${id}`);
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src={logoIcon} alt="Review Plum" className="w-10 h-10" />
            <span className="text-xl font-semibold text-foreground">Review Plum</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {/* Industries Dropdown */}
            {showIndustries && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIndustriesDropdownOpen(!industriesDropdownOpen)}
                  className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Industries
                  <ChevronDown className={`w-4 h-4 transition-transform ${industriesDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {industriesDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-card rounded-xl border border-border shadow-lg py-2 z-50">
                    {industries.map((industry) => (
                      <button
                        key={industry.path}
                        onClick={() => {
                          navigate(industry.path);
                          setIndustriesDropdownOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      >
                        <span>{industry.icon}</span>
                        {industry.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {showPricing && (
              <button
                onClick={() => scrollToSection('pricing')}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Pricing
              </button>
            )}

            {showHowItWorks && (
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                How It Works
              </button>
            )}

            <button
              onClick={() => scrollToSection('faq')}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              FAQ
            </button>
          </div>

          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="hidden sm:inline-flex"
            >
              Log In
            </Button>
            <Button 
              onClick={() => navigate('/login')}
              className="gradient-btn"
            >
              Get Started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-muted-foreground hover:text-foreground"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <div className="space-y-2">
              {showIndustries && (
                <div className="px-2 py-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Industries</p>
                  <div className="grid grid-cols-2 gap-2">
                    {industries.map((industry) => (
                      <button
                        key={industry.path}
                        onClick={() => {
                          navigate(industry.path);
                          setMobileMenuOpen(false);
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
                      >
                        <span>{industry.icon}</span>
                        {industry.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {showPricing && (
                <button
                  onClick={() => {
                    scrollToSection('pricing');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
                >
                  Pricing
                </button>
              )}
              {showHowItWorks && (
                <button
                  onClick={() => {
                    scrollToSection('how-it-works');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
                >
                  How It Works
                </button>
              )}
              <button
                onClick={() => {
                  scrollToSection('faq');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
              >
                FAQ
              </button>
              <button
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
              >
                Log In
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
